import React, { FC, useEffect, useState } from 'react';

import styles from './PortfolioEditableFields.module.scss';
import { PortfolioModel } from 'pages/PortfoliosPage/PortfolioModel';
import { PortfolioService } from 'pages/PortfoliosPage/PortfolioService';
import EditableTextField from 'pages/PortfoliosPage/PortfolioDetailsDialog/EditableTextField/EditableTextField';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface PortfolioEditableFieldsProps {
  portfolio: PortfolioModel;
  onPortfolioUpdate: (portfolioId: string) => void;
}

const PortfolioEditableFields: FC<PortfolioEditableFieldsProps> = (
  {
    portfolio,
    onPortfolioUpdate
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [displayedName, setDisplayedName] = useState(portfolio.name || '');
  const [displayedDescription, setDisplayedDescription] = useState(portfolio.description || '');
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);

  const isPersonal = portfolio.type === 'personal';

  useEffect(() => {
    setDisplayedName(portfolio?.name || '');
    setDisplayedDescription(portfolio?.description || '');
  }, [portfolio]);

  const updatePortfolioHandler = async (modifiedField: 'name' | 'description'): Promise<void> => {
    try {
      let updateResult;
      if (modifiedField === 'name') {
        updateResult = await PortfolioService.modifyPortfolio(portfolio._id, displayedName, null);
      } else if (modifiedField === 'description') {
        updateResult = await PortfolioService.modifyPortfolio(portfolio._id, null, displayedDescription);
      }

      if (!updateResult) {
       throw new Error('Error updating portfolio details');
      }

      onPortfolioUpdate(portfolio._id);
    } catch (error) {
      console.error('Failed at updating portfolio details', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at updating portfolio details. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const cancelPortfolioUpdateHandler = (modifiedField: 'name' | 'description'): void => {
    if (!portfolio) return;

    if (modifiedField === 'name') {
      setDisplayedName(portfolio.name);
    } else if (modifiedField === 'description') {
      setDisplayedDescription(portfolio.description);
    }
  };

  return (
    <div className={styles.PortfolioEditableFields} data-testid="PortfolioEditableFields">
      <EditableTextField
        label="Name"
        value={displayedName}
        isPersonal={isPersonal}
        displayedValue={displayedName}
        portfolioValue={portfolio.name}
        onValueChange={setDisplayedName}
        onUpdate={() => updatePortfolioHandler('name')}
        onCancel={() => cancelPortfolioUpdateHandler('name')}
      />
      <EditableTextField
        label="Description"
        value={displayedDescription}
        isPersonal={isPersonal}
        displayedValue={displayedDescription}
        portfolioValue={portfolio.description}
        onValueChange={setDisplayedDescription}
        onUpdate={() => updatePortfolioHandler('description')}
        onCancel={() => cancelPortfolioUpdateHandler('description')}
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default PortfolioEditableFields;
